import { createSelector } from 'reselect';
import memoize from 'lodash/memoize';
import { fg } from '@atlassian/jira-feature-gating';
import { isSubtaskHierarchyLevel } from '@atlassian/jira-issue-type-hierarchies';
import {
	SPRINT,
	BACKLOG,
	ISSUE_COUNT,
	ORIGINAL_ESTIMATE,
	STORY_POINTS,
} from '../../../model/constants';
import type { SprintId, Sprint, CardList, CardListId } from '../../../model/types';
import type { State } from '../../types';
import {
	getBacklogStrategy,
	getIsCompanyManaged,
	getIsParallelSprintsEnabled,
	isCmpKanbanProject,
} from '../app';
import { getCardLists, getVisibleCardListIds, makeOrderedVisibleCardsForCardList } from '../cards';
import { getEstimationType } from '../estimation';
import { canManageSprints } from '../permissions';
import { getInitialStatusIds, getResolutionDoneStatusIds } from '../statuses';

export const makeGetSprintEstimations = createSelector(
	[
		makeOrderedVisibleCardsForCardList,
		getInitialStatusIds,
		getResolutionDoneStatusIds,
		getEstimationType,
		isCmpKanbanProject,
	],
	(
		getOrderedVisibleCardsForCardList,
		initialStatusIds,
		resolutionDoneStatusIds,
		estimationType,
		isCmpKanban,
	) => {
		const getSprintEstimations = (cardListId: string) => {
			let list = getOrderedVisibleCardsForCardList(cardListId);

			if (!isCmpKanban) {
				list = list.filter((card) => !isSubtaskHierarchyLevel(card.typeHierarchyLevel));
			}

			const estimations = {
				NOT_STARTED: 0,
				IN_PROGRESS: 0,
				DONE: 0,
			};

			list.forEach((card) => {
				const incrementTypes = Object.freeze({
					[ORIGINAL_ESTIMATE]: card.estimate?.originalEstimate?.value || 0,
					[STORY_POINTS]: card.estimate?.storyPoints || 0,
					[ISSUE_COUNT]: 1,
				});

				const increment = incrementTypes[estimationType] || 0;

				if (initialStatusIds.includes(card.statusId)) {
					estimations.NOT_STARTED += increment;
				} else if (resolutionDoneStatusIds.includes(card.statusId)) {
					estimations.DONE += increment;
				} else {
					estimations.IN_PROGRESS += increment;
				}
			});

			return estimations;
		};

		return memoize(getSprintEstimations);
	},
);

export const makeIsCardListActiveSprint = createSelector([getCardLists], (cardLists) => {
	const isCardListActiveSprint = (cardListId: string): boolean =>
		Boolean(
			cardLists[cardListId]?.type === SPRINT &&
				cardLists[cardListId]?.sprintData?.state === 'ACTIVE',
		);

	return isCardListActiveSprint;
});

export const makeIsCardListFutureSprint = createSelector([getCardLists], (cardLists) => {
	const isCardListFutureSprint = (cardListId: string): boolean =>
		Boolean(
			cardLists[cardListId]?.type === SPRINT &&
				cardLists[cardListId]?.sprintData?.state === 'FUTURE',
		);

	return isCardListFutureSprint;
});

export const makeIsSprintCardList = createSelector(
	[getCardLists],
	(cardLists) =>
		(cardListId: string): boolean =>
			Boolean(cardLists && cardLists[cardListId]?.type === SPRINT),
);

export const getSprintName = (state: State, sprintId: SprintId): string =>
	getCardLists(state)[String(sprintId)].name;

export const getSprint = (state: State, sprintId: SprintId): Sprint | null =>
	getCardLists(state)[String(sprintId)].sprintData || null;

export const getAllSprints = createSelector(
	[getVisibleCardListIds, getCardLists],
	(cardListIds, cardLists) =>
		cardListIds
			.map((cardListId) => cardLists[cardListId])
			.filter((cardList) => cardList.type === SPRINT),
);

export const makeGetOtherOrderedSprints = createSelector(
	[getVisibleCardListIds, getCardLists],
	(cardListIds, cardLists) => {
		const getOtherOrderedSprints: (arg1: string) => CardList[] = (id) =>
			cardListIds
				.map((cardListId) => cardLists[cardListId])
				.filter((cardList) => cardList.type === SPRINT && cardList.id !== id);

		return memoize(getOtherOrderedSprints);
	},
);

export const makeGetNextSprintId = createSelector([getVisibleCardListIds], (cardListIds) => {
	const getNextSprintId = (sprintId: string): string | null => {
		const index = cardListIds.indexOf(sprintId);
		const nextCardListIndex = index + 1;
		if (
			index === -1 ||
			index === cardListIds.length - 1 ||
			cardListIds[nextCardListIndex] === BACKLOG
		) {
			return null;
		}
		return cardListIds[nextCardListIndex];
	};

	return getNextSprintId;
});

export const makeGetPreviousNotStartedSprintId = createSelector(
	[getVisibleCardListIds, getCardLists],
	(cardListIds, cardLists) => {
		const getPrevNotStartedSprintId = (sprintId: string): string | null => {
			const index = cardListIds.indexOf(sprintId);
			const previousCardListIndex = index - 1;
			const previousCardListId = cardListIds[previousCardListIndex];
			if (
				index === -1 ||
				index === 0 ||
				cardLists[previousCardListId].type !== SPRINT ||
				!cardLists[previousCardListId].sprintData ||
				// @ts-expect-error - TS2532 - Object is possibly 'undefined'. | TS2532 - Object is possibly 'undefined'.
				cardLists[previousCardListId].sprintData.state !== 'FUTURE'
			) {
				return null;
			}
			return previousCardListId;
		};

		return getPrevNotStartedSprintId;
	},
);

export const getFirstInactiveSprintId = createSelector(
	[getCardLists, getVisibleCardListIds],
	(cardLists, ids) =>
		ids.filter((id) => {
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			const card = cardLists[id] as CardList;
			return card.type === SPRINT && card.sprintData?.state === 'FUTURE';
		})[0],
);

export const getLastInactiveSprintId = createSelector(
	[getCardLists, getVisibleCardListIds],
	(cardLists, ids) => {
		const unscheduledSprints = ids.filter((id) => {
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			const card = cardLists[id] as CardList;
			return card.type === SPRINT && card.sprintData?.state === 'FUTURE';
		});
		return unscheduledSprints[unscheduledSprints.length - 1];
	},
);

export const makeCanEditSprint = createSelector([getCardLists], (cardLists) => {
	const getCanEditSprint = (id: CardListId): boolean =>
		Boolean(
			cardLists[id] &&
				cardLists[id].type === SPRINT &&
				cardLists[id].sprintData &&
				// @ts-expect-error - TS2532 - Object is possibly 'undefined'. | TS2532 - Object is possibly 'undefined'.
				cardLists[id].sprintData.canUpdateSprint,
		);
	return getCanEditSprint;
});

export const makeGetSprint = createSelector([getCardLists], (cardLists) => {
	const getSprintById: (arg1: CardListId) => Sprint | null = (id) =>
		// @ts-expect-error - TS2322 - Type 'Sprint | null | undefined' is not assignable to type 'Sprint | null'. | TS2532 - Object is possibly 'undefined'. | TS2532 - Object is possibly 'undefined'.
		cardLists[id] && cardLists[id].sprintData ? cardLists[id].sprintData : null;
	return memoize(getSprintById);
});

export const isBacklogWithSprints = createSelector(
	[getBacklogStrategy],
	(backlogStrategy) => backlogStrategy === SPRINT,
);

export const makeIsSprintMenuVisible = createSelector(
	[isBacklogWithSprints, makeGetSprint, makeCanEditSprint],
	(areSprintsEnabled, getSprintById, canEditSprint) => {
		const isSprintMenuVisible = (id: CardListId): boolean => {
			if (!areSprintsEnabled) {
				return false;
			}

			const sprint = getSprintById(id);

			if (!sprint) {
				return false;
			}

			return canEditSprint(id);
		};
		return isSprintMenuVisible;
	},
);

export const makeIsStartSprintVisible = createSelector(
	[
		isBacklogWithSprints,
		makeCanEditSprint,
		makeIsCardListFutureSprint,
		getIsParallelSprintsEnabled,
		getIsCompanyManaged,
	],
	(
		areSprintsEnabled,
		canEditSprint,
		isFutureSprint,
		isParallelSprintsEnabled,
		isCompanyManaged,
	) => {
		const isStartSprintVisible = (id: CardListId, isFirstList: boolean): boolean => {
			if (fg('jsw-jet-yamato-fe-permissions-seq-sprint-actions')) {
				if (!areSprintsEnabled || (isCompanyManaged && !isParallelSprintsEnabled && !isFirstList)) {
					return false;
				}
			} else if (
				!areSprintsEnabled ||
				!canEditSprint(id) ||
				(isCompanyManaged && !isParallelSprintsEnabled && !isFirstList)
			) {
				return false;
			}

			return isFutureSprint(id);
		};

		return isStartSprintVisible;
	},
);

export const makeIsCreateSprintVisible = createSelector(
	[isBacklogWithSprints, canManageSprints],
	(areSprintsEnabled, sprintsManageEnabled) => {
		const isCreateSprintVisible = (id: CardListId): boolean => {
			if (!areSprintsEnabled || !sprintsManageEnabled) {
				return false;
			}

			return id === BACKLOG;
		};

		return isCreateSprintVisible;
	},
);

export const makeIsStartSprintEnabled = createSelector(
	[getIsCompanyManaged, getIsParallelSprintsEnabled, makeIsStartSprintVisible],
	(isCompanyManaged, isParallelSprintsEnabled, isStartSprintVisible) =>
		(id: CardListId, isFirstList: boolean): boolean => {
			if (!isStartSprintVisible(id, isFirstList)) {
				return false;
			}
			// First list in backlog always has start sprint button enabled regardless
			if (isFirstList) {
				return true;
			}
			// Agility Boards always have parallel sprints enabled
			return isCompanyManaged ? isParallelSprintsEnabled : true;
		},
);

export const makeIsCompleteSprintVisible = createSelector(
	[isBacklogWithSprints, makeCanEditSprint, makeIsCardListActiveSprint],
	(areSprintsEnabled, canEditSprint, isActiveSprint) => {
		const isCompleteSprintVisible = (id: CardListId): boolean =>
			fg('jsw-jet-yamato-fe-permissions-seq-sprint-actions') // shows the button but disabled based on permission
				? areSprintsEnabled && isActiveSprint(id)
				: areSprintsEnabled && canEditSprint(id) && isActiveSprint(id);

		return isCompleteSprintVisible;
	},
);

export const makeIsAddSprintDatesButtonVisible = createSelector(
	[
		(state: State) => getCardLists(state),
		(state: State) => makeCanEditSprint(state),
		(state: State) => isBacklogWithSprints(state),
		// @ts-expect-error - TS7031 - Binding element 'cardListId' implicitly has an 'any' type.
		(state: State, { cardListId }) => cardListId,
	],
	(cardLists, canEditSprint, areSprintsEnabled, cardListId) =>
		Boolean(
			areSprintsEnabled &&
				canEditSprint(cardListId) &&
				cardLists[cardListId].sprintData &&
				!cardLists[cardListId].sprintData?.startDate &&
				!cardLists[cardListId].sprintData?.endDate,
		),
);
